// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { VariantId } from '../types';

/**
 * Escape-variant enrolment.
 *
 * EXACT MATCHING GIVES YOU PRECISION AND IT INHERITS GREP'S RECALL. Measured across 15 needles on a
 * real Vite 8.2.1 build: `grep -rF` found 9 of 14 shipped values, and a byte-exact matcher found
 * exactly the same 9, hit for hit. The five misses were not exotic:
 *
 *   - a value containing a backslash        `tok_c:\Users\me\key`  ships as  `tok_c:\\Users\\me\\key`
 *   - a value containing `</script>`        ships as `<\/script>` in EVERY quote context
 *   - a tab, U+2028, U+2029, NUL, a lone surrogate
 *   - a dotenv-expanded value (handled in envexpand.ts, not here)
 *
 * With the variants below enrolled, the same fixture scores 14/14.
 *
 * TWO MEASURED FACTS THAT SHAPE THIS FILE:
 *
 * 1. THE `\xNN` FEAR IS WRONG FOR VITE. Vite hard-sets esbuild's `charset: "utf8"`
 *    (`charset: esbuildOptions.charset ?? "utf8"`), so `sénhã-très-£42` and `emoji-🔑-key` ship
 *    VERBATIM. Standalone esbuild defaults to `ascii` and does emit `\xE9`, so the ASCII-escaped
 *    forms are enrolled — but as a minority case, not the expectation.
 *
 * 2. QUOTE CONTEXT IS CHOSEN PER STRING TO MINIMISE ESCAPES. Vite 8 (rolldown/oxc) defaults to
 *    BACKTICKS, in which `"` and `'` are NOT escaped; terser and SWC use double quotes and emit
 *    `"line1\nline2"` where esbuild/oxc emit a literal newline inside a template literal. Both
 *    newline forms are therefore enrolled.
 *
 * A variant is kept ONLY IF `transform(v) !== v`. For a typical 15-secret `.env` that is +4 to +8
 * patterns, not +90.
 */

const enc = new TextEncoder();

export interface Variant {
  readonly id: VariantId;
  readonly text: string;
}

/** Escape a string the way a JS minifier would when emitting it inside `quote`. */
function jsEscape(value: string, quote: '"' | "'" | '`'): string {
  let out = '';
  for (const ch of value) {
    const c = ch.codePointAt(0)!;
    if (ch === '\\') out += '\\\\';
    else if (ch === quote) out += `\\${quote}`;
    else if (ch === '\n') out += '\\n';
    else if (ch === '\r') out += '\\r';
    else if (ch === '\t') out += '\\t';
    else if (ch === '\b') out += '\\b';
    else if (ch === '\f') out += '\\f';
    else if (ch === '\v') out += '\\v';
    else if (c === 0) out += '\\0';
    else if (c === 0x2028) out += '\\u2028';
    else if (c === 0x2029) out += '\\u2029';
    else if (c < 0x20) out += `\\x${c.toString(16).padStart(2, '0')}`;
    // A backtick-quoted string must also escape `${`, which would otherwise open an interpolation.
    else if (quote === '`' && ch === '$') out += '$';
    else out += ch;
  }
  // `${` inside a template literal is a substitution opener and gets escaped as `\${`.
  if (quote === '`') out = out.replace(/\$\{/g, '\\${');
  return out;
}

/** The ASCII-only escaping esbuild uses with `charset: 'ascii'`. */
function asciiEscape(value: string, upper: boolean): string {
  let out = '';
  for (const ch of value) {
    const c = ch.codePointAt(0)!;
    if (c < 0x80) {
      out += ch;
      continue;
    }
    if (c > 0xffff) {
      // esbuild emits `\u{1F600}`; terser emits a surrogate pair as two `\uXXXX` escapes.
      if (upper) {
        out += `\\u{${c.toString(16).toUpperCase()}}`;
      } else {
        const v = c - 0x10000;
        const hi = 0xd800 + (v >> 10);
        const lo = 0xdc00 + (v & 0x3ff);
        out += `\\u${hi.toString(16)}\\u${lo.toString(16)}`;
      }
      continue;
    }
    if (c <= 0xff) {
      const hex = c.toString(16).padStart(2, '0');
      out += `\\x${upper ? hex.toUpperCase() : hex}`;
    } else {
      const hex = c.toString(16).padStart(4, '0');
      out += `\\u${upper ? hex.toUpperCase() : hex}`;
    }
  }
  return out;
}

/**
 * All the forms of `value` a bundler could plausibly have written into a build, deduplicated, with
 * the raw form always first. Each is kept only when it actually differs from the raw string.
 */
export function variantsOf(value: string): Variant[] {
  const out: Variant[] = [{ id: 'raw', text: value }];
  const seen = new Set([value]);
  const push = (id: VariantId, text: string) => {
    if (text === value || seen.has(text)) return;
    seen.add(text);
    out.push({ id, text });
  };

  push('js-double', jsEscape(value, '"'));
  push('js-single', jsEscape(value, "'"));
  push('js-backtick', jsEscape(value, '`'));

  // `</script>` is escaped to `<\/script>` inside a string literal in EVERY quote context, because
  // an unescaped one would terminate an inline <script> block. This is a real miss, not a curiosity.
  if (value.includes('</')) push('script-close', value.replace(/<\//g, '<\\/'));

  // JSON.stringify with HTML-safe escaping, as several serialisers emit.
  if (/[<>&\u2028\u2029]/.test(value)) {
    push(
      'json-html',
      value
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026')
        .replace(/\u2028/g, '\\u2028')
        .replace(/\u2029/g, '\\u2029'),
    );
  }

  try {
    push('uri-component', encodeURIComponent(value));
  } catch {
    /* a lone surrogate throws URIError; there is simply no URI form of it */
  }

  // A multiline value (a PEM key is the case that matters) can be written either with a literal
  // newline inside a template literal, or with `\n` inside a double-quoted string.
  if (value.includes('\n')) push('terser-newline', value.replace(/\n/g, '\\n'));

  if (/[^\x00-\x7f]/.test(value)) {
    push('ascii-escape-lower', asciiEscape(value, false));
    push('ascii-escape-upper', asciiEscape(value, true));
  }

  return out;
}

/**
 * UTF-8 bytes for a variant.
 *
 * The automaton runs over BYTES, never over a decoded string, and that is provably safe rather than
 * merely convenient: UTF-8 is self-synchronising, so a valid UTF-8 needle can only ever match at a
 * character boundary. Verified exhaustively — haystack built from every one of the 1,112,064 Unicode
 * scalar values, needles `é ✓ 😀 naïve 日本`, zero non-boundary matches.
 *
 * Decoding the haystack instead would be actively wrong: a non-fatal TextDecoder substitutes U+FFFD
 * for every invalid sequence, which shifts every subsequent offset and can destroy a match sitting
 * inside a binary asset.
 */
export function variantBytes(v: Variant): Uint8Array {
  return enc.encode(v.text);
}
